<script lang="ts">
  import { auth } from "$lib/stores/auth.svelte";
  import { navigate } from "$lib/router.svelte";
  import { theme } from "$lib/stores/theme.svelte";
  import { meStore, type Persona } from "$lib/stores/me.svelte";
  import { toasts } from "$lib/stores/toasts.svelte";
  import Link from "$lib/components/Link.svelte";
  import PrivacyToggle from "$lib/components/PrivacyToggle.svelte";

  let menuOpen = $state(false);
  let personaMenu = $state(false);

  $effect(() => {
    if (auth.user && !meStore.me && !meStore.loading) meStore.load();
  });

  $effect(() => {
    function onPin() { navigate("/settings#privacy"); }
    window.addEventListener("takite:privacy-need-pin", onPin);
    return () => window.removeEventListener("takite:privacy-need-pin", onPin);
  });

  async function logout() {
    meStore.clear();
    await auth.logout();
    navigate("/login");
  }

  const personaInfo: Record<Persona, { emoji: string; label: string }> = {
    personal: { emoji: "🙂", label: "Pessoal" },
    family:   { emoji: "👨‍👩‍👧", label: "Família" },
    business: { emoji: "💼", label: "Negócio" },
  };

  async function setPersona(p: Persona) {
    personaMenu = false;
    if (!meStore.me) return;
    try {
      // Troca persona cosmética
      if (meStore.me.persona !== p) await meStore.setPersona(p);
      // Troca workspace ativo para o do mesmo tipo
      const ws = auth.workspaces.find((w: any) => w.type === p);
      if (ws) {
        auth.selectWorkspace(ws.id);
        toasts.success(`Perfil: ${personaInfo[p].label}`);
        // Recarrega dados do workspace selecionado
        window.dispatchEvent(new CustomEvent("takite:workspace-changed"));
      }
    } catch {
      toasts.error("Falha ao atualizar perfil");
    }
  }

  function toggleMenu(e: MouseEvent) {
    e.stopPropagation();
    menuOpen = !menuOpen;
  }

  function closeOnOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (personaMenu && !target.closest(".persona-dd")) personaMenu = false;
  }
</script>

<svelte:window onclick={closeOnOutside} />

<header class="surface border-b" style="border-color: var(--border)">
  <div class="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
    <div class="flex items-center gap-6">
      <Link to="/" class="text-xl font-bold text-brand">TáKite</Link>
      <nav class="hidden md:flex gap-4 text-sm" aria-label="Principal">
        <Link to="/" class="hover:text-brand">Painel</Link>
        <Link to="/transactions" class="hover:text-brand">Transações</Link>
        <Link to="/insights" class="hover:text-brand">Insights</Link>
        <Link to="/recorrencias" class="hover:text-brand">Recorrências</Link>
        <Link to="/fidelidade" class="hover:text-brand">Fidelidade</Link>
        <Link to="/categories" class="hover:text-brand">Categorias</Link>
        <Link to="/workspaces" class="hover:text-brand">Workspaces</Link>
        <Link to="/pricing" class="hover:text-brand">Planos</Link>
        <Link to="/settings" class="hover:text-brand">Config</Link>
      </nav>
    </div>

    <div class="flex items-center gap-2 min-w-0">
      {#if auth.user && meStore.me}
        <div class="relative persona-dd hidden sm:block">
          <button
            type="button"
            class="btn-ghost text-sm flex items-center gap-1"
            aria-haspopup="menu"
            aria-expanded={personaMenu}
            title="Alternar perfil"
            onclick={(e) => { e.stopPropagation(); personaMenu = !personaMenu; }}
          >
            <span aria-hidden="true">{personaInfo[meStore.me.persona].emoji}</span>
            <span>{personaInfo[meStore.me.persona].label}</span>
            <span class="text-xs opacity-60">▾</span>
          </button>
          {#if personaMenu}
            <div role="menu" class="persona-menu">
              {#each (["personal","family","business"] as Persona[]) as p}
                <button
                  role="menuitemradio"
                  aria-checked={meStore.me.persona === p}
                  class="persona-item"
                  class:active={meStore.me.persona === p}
                  onclick={() => setPersona(p)}
                >
                  <span aria-hidden="true">{personaInfo[p].emoji}</span>
                  <span>{personaInfo[p].label}</span>
                  {#if meStore.me.persona === p}<span class="ml-auto">✓</span>{/if}
                </button>
              {/each}
            </div>
          {/if}
        </div>
        <PrivacyToggle hasPin={!!meStore.me.has_privacy_pin} compact />
      {/if}
      <button class="btn-ghost" aria-label="Alternar tema" title="Modo escuro/claro"
              onclick={theme.toggle}>
        {theme.isDark ? "☀️" : "🌙"}
      </button>
      {#if auth.user}
        <span class="hidden sm:inline text-sm text-muted truncate max-w-[140px]">{auth.user.name}</span>
        <button onclick={logout} class="hidden md:inline-flex btn-secondary text-sm">Sair</button>
      {/if}
      <button type="button" class="btn-ghost md:hidden" aria-label="Menu" aria-expanded={menuOpen}
              onclick={toggleMenu}>
        ☰
      </button>
    </div>
  </div>

  {#if menuOpen}
    <nav class="md:hidden border-t flex flex-col p-2" style="border-color: var(--border)" aria-label="Menu mobile">
      {#if auth.user}
        <div class="px-3 py-2 text-xs text-muted truncate">{auth.user.name}</div>
      {/if}

      {#if meStore.me}
        <div class="px-3 py-2">
          <div class="text-xs text-muted mb-1">Perfil</div>
          <div class="grid grid-cols-3 gap-1">
            {#each (["personal","family","business"] as Persona[]) as p}
              <button
                type="button"
                class="persona-pill"
                class:active={meStore.me.persona === p}
                onclick={() => setPersona(p)}
              >
                <span aria-hidden="true">{personaInfo[p].emoji}</span>
                <span class="text-xs">{personaInfo[p].label}</span>
              </button>
            {/each}
          </div>
        </div>
      {/if}

      <Link to="/" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Painel</Link>
      <Link to="/transactions" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Transações</Link>
      <Link to="/insights" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Insights</Link>
      <Link to="/recorrencias" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Recorrências</Link>
      <Link to="/fidelidade" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Fidelidade</Link>
      <Link to="/categories" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Categorias</Link>
      <Link to="/workspaces" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Workspaces</Link>
      <Link to="/pricing" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Planos</Link>
      <Link to="/settings" class="px-3 py-3 rounded hover:bg-slate-100 dark:hover:bg-slate-800" onNav={() => (menuOpen = false)}>Config</Link>
      {#if auth.user}
        <button type="button" class="text-left px-3 py-3 rounded text-red-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                onclick={() => { menuOpen = false; logout(); }}>Sair</button>
      {/if}
    </nav>
  {/if}
</header>

<style>
  .persona-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    min-width: 11rem;
    background: #ffffff;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    box-shadow: 0 10px 25px rgba(0,0,0,0.12);
    overflow: hidden;
    z-index: 50;
  }
  :global(.dark) .persona-menu { background: #18181b; }
  .persona-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.55rem 0.8rem;
    font-size: 0.85rem;
    text-align: left;
  }
  .persona-item:hover { background: rgba(99,102,241,0.08); }
  .persona-item.active { color: rgb(99,102,241); font-weight: 600; }
  .persona-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    padding: 0.45rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
  }
  .persona-pill.active {
    border-color: rgb(99,102,241);
    background: rgba(99,102,241,0.1);
  }
</style>
